import { MidiData, MidiEvent, MidiNoteOffEvent, MidiNoteOnEvent, MidiTrackNameEvent } from 'midi-file';
import { Voice } from './model.ts';
import { capitalize } from './util.ts';

type MidiNoteEvent = MidiNoteOnEvent | MidiNoteOffEvent;

const firstNoteDuration = 8192; // in midi "ticks"

export function getFirstNoteMidi(midi: MidiData, voice: Voice, startTick = 0): MidiData | undefined {
  const { header, tracks } = midi;
  let firstNoteOnEventForVoice: MidiNoteEvent | undefined;
  const channel = getChannelForVoice(voice);
  for (const track of tracks) {
    // Voice tracks are identified by track name (channels are assigned by staff index, so they
    // only match getChannelForVoice for full SATB songs).  Tracks without a recognized voice
    // name fall back to the channel mapping.
    const trackVoice = getTrackVoice(track);
    let tickCount = 0;
    for (const event of track) {
      tickCount += event.deltaTime;
      if (event.type !== 'noteOn' || tickCount < startTick) {
        continue;
      }
      if (trackVoice !== undefined ? trackVoice === voice : event.channel === channel) {
        firstNoteOnEventForVoice = event;
        break;
      }
    }
    if (firstNoteOnEventForVoice) {
      break;
    }
  }
  if (!firstNoteOnEventForVoice) {
    return;
  }
  return {
    header: { ...header, numTracks: 1 },
    tracks: [
      [
        // name the track so that audio generation attributes the note to the requested voice
        { type: 'trackName', deltaTime: 0, text: capitalize(voice) },
        { ...firstNoteOnEventForVoice, deltaTime: 0 },
        { ...firstNoteOnEventForVoice, type: 'noteOff', deltaTime: firstNoteDuration },
      ],
    ],
  };
}

function getTrackVoice(track: MidiEvent[]): Voice | undefined {
  const name = (track.find(({ type }) => type === 'trackName') as MidiTrackNameEvent | undefined)?.text?.toLowerCase();
  return name && Object.values(Voice).includes(name as Voice) ? (name as Voice) : undefined;
}

// The channel fallback is intentionally maintained for maximum compatibility with arbitrary MIDI
// files that lack voice track names, such as author-provided or notation-software-exported files
// (e.g. the Tanglewood Hymnal LilyPond exports, which use channels 0-3 in SATB order).
function getChannelForVoice(voice: Voice): number {
  switch (voice) {
    default:
    case Voice.Soprano:
      return 0;
    case Voice.Alto:
      return 1;
    case Voice.Tenor:
      return 2;
    case Voice.Bass:
      return 3;
    case Voice.Piano:
      return 4;
  }
}
